import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Fade,
  Card,
  CardContent,
  Container,
  CircularProgress,
  TextField,
  Backdrop,
  Grid,
  Switch,
  Paper,
  Snackbar,
  Alert,
  FormControlLabel,
  Checkbox,
  Modal,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import axios from 'axios';
import { FaChartLine, FaUsers, FaUser, FaSignOutAlt, FaBars, FaSyncAlt, FaUserPlus, FaUserEdit, FaThLarge, FaTh } from 'react-icons/fa';
import { Person, EmojiEvents } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { format } from 'date-fns';
import LanguageSwitcher from '../components/LanguageSwitcher';
import UserSignals from './UserSignals';
import UserPayment from './UserPayment';
import UserProfits from './UserProfits';

const ENCRYPTION_KEY = 'dadash123@@123##';

const Subscribers = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useState({});
  const [hasParentID, setHasParentID] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [filteredSubscribers, setFilteredSubscribers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [viewMode, setViewMode] = useState('single');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const [openAddUserModal, setOpenAddUserModal] = useState(false);
  const [openEditUserModal, setOpenEditUserModal] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    family: '',
    startDate: null,
    endDate: null,
    nationalCode: '',
    mobileNO: '',
    isVIP: false,
  });
  const [editUser, setEditUser] = useState({
    userID: '',
    name: '',
    family: '',
    startDate: null,
    endDate: null,
    nationalCode: '',
    mobileNO: '',
    isVIP: false,
  });
  const [newUserID, setNewUserID] = useState(null);
  const stickyRef = useRef(null);
  const [selectedCardIndex, setSelectedCardIndex] = useState(null);
  const [openSignalsModal, setOpenSignalsModal] = useState(false);
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [openProfitsModal, setOpenProfitsModal] = useState(false);
  const [selectedUserID, setSelectedUserID] = useState(null);
  const [selectedParentID, setSelectedParentID] = useState(null);
  const [selectedUserKind, setSelectedUserKind] = useState(null);

  const handleCardSelect = (index) => {
    if (selectedCardIndex === index) {
      setSelectedCardIndex(null);
    } else {
      setSelectedCardIndex(index);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('subscribers');
    navigate('/');
  };

  useEffect(() => {
    const encryptedData = localStorage.getItem('user');
    if (encryptedData) {
      try {
        const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);
        const userData = JSON.parse(decrypted);
        setUser(userData);
        const parentIdExists = userData.ParentID && userData.ParentID !== "00000000-0000-0000-0000-000000000000";
        setHasParentID(parentIdExists);
      } catch (err) {
        console.error('Failed to decrypt user data:', err);
        localStorage.removeItem('user');
        navigate('/');
      }
    } else {
      console.warn('No encrypted data found');
      navigate('/');
    }
  }, [navigate]);

  useEffect(() => {
    if (user.UserID) {
      fetchSubscribers();
    }
  }, [user]);

  useEffect(() => {
    if (searchText) {
      const lowerSearch = searchText.toLowerCase();
      const filtered = subscribers.filter((subscriber) => {
        return (
          (subscriber.Name?.toLowerCase().includes(lowerSearch) || false) ||
          (subscriber.Family?.toLowerCase().includes(lowerSearch) || false) ||
          (subscriber.EndDate?.toLowerCase().includes(lowerSearch) || false) ||
          (subscriber.TotalProfit?.toString().toLowerCase().includes(lowerSearch) || false) ||
          (subscriber.TotalWin?.toString().toLowerCase().includes(lowerSearch) || false) ||
          (subscriber.TotalLoss?.toString().toLowerCase().includes(lowerSearch) || false)
        );
      });
      setFilteredSubscribers(filtered);
      if (filtered.length === 0) {
        setSnackbarMessage(t('noResults'));
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
      }
    } else {
      setFilteredSubscribers(subscribers);
      if (subscribers.length === 0 && !loading) {
        setSnackbarMessage(t('noData'));
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
      }
    }
  }, [searchText, subscribers, t, loading]);

  useEffect(() => {
    if (stickyRef.current) {
      //console.log('Sticky Box position:', stickyRef.current.getBoundingClientRect());
      //console.log('Sticky Box height:', stickyRef.current.offsetHeight);
    }
  }, []);

  const fetchSubscribers = async () => {
    setLoading(true);
    setSnackbarOpen(false);

    try {
      const userKind = user.Kind === 1 ? 2 : 3;
      const response = await axios.post(
        'https://copyapi.avangar.ir/api/forex/userlist',
        {
          parentId: user.UserID,
          userKind: userKind,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
        }
      );

      const encryptedData = CryptoJS.AES.encrypt(JSON.stringify(response.data), ENCRYPTION_KEY).toString();
      localStorage.setItem('subscribers', encryptedData);

      setSubscribers(response.data || []);
    } catch (err) {
      console.error('Subscribers error:', err);
      setSnackbarMessage(err.response?.data?.Message || t('error'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);

      const encryptedData = localStorage.getItem('subscribers');
      if (encryptedData) {
        try {
          const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
          const decrypted = bytes.toString(CryptoJS.enc.Utf8);
          setSubscribers(JSON.parse(decrypted));
        } catch (decryptErr) {
          console.error('Failed to decrypt subscribers data:', decryptErr);
          setSubscribers([]);
        }
      } else {
        setSubscribers([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (index) => {
    const updatedSubscribers = [...subscribers];
    const newStatus = !updatedSubscribers[index].Active;
    const userId = updatedSubscribers[index].UserID;

    setLoading(true);
    setSnackbarOpen(false);

    try {
      const response = await axios.post(
        'https://copyapi.avangar.ir/api/forex/UpdateUserStatus',
        {
          UserID: userId,
          Status: newStatus,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
        }
      );

      if (response.data.Message === 'User Update Successfully') {
        updatedSubscribers[index].Active = newStatus;
        setSubscribers(updatedSubscribers);
        setSnackbarMessage(t('statusUpdated'));
        setSnackbarSeverity('success');
      } else {
        setSnackbarMessage(response.data.Message || 'Unexpected response from server');
        setSnackbarSeverity('warning');
      }
      setSnackbarOpen(true);
    } catch (err) {
      if (err.response?.data?.Message !== 'User Update Successfully') {
        console.error('UpdateUserStatus error:', err);
      }

      if (err.response?.data?.Message === 'User Update Successfully') {
        updatedSubscribers[index].Active = newStatus;
        setSubscribers(updatedSubscribers);
        setSnackbarMessage(t('statusUpdated'));
        setSnackbarSeverity('success');
      } else {
        setSnackbarSeverity('error');
        setSnackbarMessage(err.response?.data?.Message || 'Failed to update user status');
      }
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyUserID = (userID) => {
    navigator.clipboard.writeText(userID).then(() => {
      setSnackbarMessage(t('copied'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    }).catch((err) => {
      console.error('Failed to copy userID:', err);
      setSnackbarMessage('Failed to copy User ID');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    });
  };
/*
  const handleCopyNewUserID = () => {
    if (newUserID) {
      navigator.clipboard.writeText(newUserID).then(() => {
        setSnackbarMessage(t('copied'));
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }).catch((err) => {
        console.error('Failed to copy new userID:', err);
      });
    }
  };
*/
  const handleAddUser = () => {
    setOpenAddUserModal(true);
  };

  const handleEditUser = () => {
    if (selectedCardIndex !== null) {
      const selectedSubscriber = filteredSubscribers[selectedCardIndex];
      setEditUser({
        userID: selectedSubscriber.UserID,
        name: selectedSubscriber.Name || '',
        family: selectedSubscriber.Family || '',
        startDate: selectedSubscriber.StartDate ? new Date(selectedSubscriber.StartDate) : null,
        endDate: selectedSubscriber.EndDate ? new Date(selectedSubscriber.EndDate) : null,
        nationalCode: selectedSubscriber.NationalCode || '',
        mobileNO: selectedSubscriber.MobileNO || '',
        isVIP: selectedSubscriber.UKind === 4,
      });
      setOpenEditUserModal(true);
    }
  };

  const handleCloseAddUserModal = () => {
    setOpenAddUserModal(false);
    setNewUser({
      name: '',
      family: '',
      startDate: null,
      endDate: null,
      nationalCode: '',
      mobileNO: '',
      isVIP: false,
    });
  };

  const handleCloseEditUserModal = () => {
    setOpenEditUserModal(false);
    setEditUser({
      userID: '',
      name: '',
      family: '',
      startDate: null,
      endDate: null,
      nationalCode: '',
      mobileNO: '',
      isVIP: false,
    });
  };

  const handleAddUserSubmit = async () => {
    setLoading(true);
    setSnackbarOpen(false);

    try {
      let userKind;
      if (!user.ParentID) {
        userKind = 2;
      } else {
        userKind = newUser.isVIP ? 4 : 3;
      }

      const payload = {
        parentID: user.UserID,
        name: newUser.name,
        Family: newUser.family,
        mobileNO: newUser.mobileNO,
        nationalCode: newUser.nationalCode,
        userKind: userKind,
        startDate: newUser.startDate ? format(newUser.startDate, 'yyyy-MM-dd') : '',
        endDate: newUser.endDate ? format(newUser.endDate, 'yyyy-MM-dd') : '',
        Active: true,
      };

      const response = await axios.post(
        'https://copyapi.avangar.ir/api/forex/Adduser',
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
        }
      );

      if (response.data.Message === 'The user already exists.') {
        setSnackbarMessage(t('userExists'));
        setSnackbarSeverity('error');
      } else if (response.data.UserID) {
        setNewUserID(response.data.UserID);
        setSnackbarMessage(
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {t('userAdded')}{' '}
            <span
              onClick={() => navigator.clipboard.writeText(response.data.UserID)}
              style={{
                cursor: 'pointer',
                color: '#1e3c72',
                textDecoration: 'underline',
              }}
            >
              {response.data.UserID}
            </span>
          </Box>
        );
        setSnackbarSeverity('success');
        fetchSubscribers();
        handleCloseAddUserModal();
      } else {
        setSnackbarMessage(t('unexpectedResponse'));
        setSnackbarSeverity('warning');
      }
      setSnackbarOpen(true);
    } catch (err) {
      console.error('AddUser error:', err);
      setSnackbarMessage(err.response?.data?.Message || t('addUserError'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUserSubmit = async () => {
    setLoading(true);
    setSnackbarOpen(false);

    try {
      const userKind = editUser.isVIP ? 4 : 3;
      const payload = {
        UserID: editUser.userID,
        Name: editUser.name,
        Family: editUser.family,
        MobileNO: editUser.mobileNO,
        NationalCode: editUser.nationalCode,
        StartDate: editUser.startDate ? format(editUser.startDate, 'yyyy-MM-dd') : '',
        EndDate: editUser.endDate ? format(editUser.endDate, 'yyyy-MM-dd') : '',
        UserKind: userKind,
      };

      const response = await axios.post(
        'https://copyapi.avangar.ir/api/forex/useredit',
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
        }
      );

      if (response.data.Message === 'User Update Successfully') {
        setSnackbarMessage(t('successUpdate'));
        setSnackbarSeverity('success');
        fetchSubscribers();
        handleCloseEditUserModal();
      } else {
        setSnackbarMessage(t('unexpectedResponse'));
        setSnackbarSeverity('warning');
      }
      setSnackbarOpen(true);
    } catch (err) {
      console.error('EditUser error:', err);
      setSnackbarMessage(err.response?.data?.Message || t('editUserError'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSignalClick = (userID, parentID, userKind) => {
    setSelectedUserID(userID);
    setSelectedParentID(parentID);
    setSelectedUserKind(userKind);
    setOpenSignalsModal(true);
  };

  const handlePaymentClick = (userID) => {
    setSelectedUserID(userID);
    setOpenPaymentModal(true);
  };

  const handleProfitsClick = (userID) => {
    setSelectedUserID(userID);
    setOpenProfitsModal(true);
  };

  const handleCloseSignalsModal = () => {
    setOpenSignalsModal(false);
  };

  const handleClosePaymentModal = () => {
    setOpenPaymentModal(false);
  };

  const handleCloseProfitsModal = () => {
    setOpenProfitsModal(false);
  };

  const handleRefresh = () => {
    fetchSubscribers();
  };

  const handleSingleColumn = () => {
    setViewMode('single');
  };

  const handleDoubleColumn = () => {
    setViewMode('double');
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setNewUserID(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  const getTypeLabel = (Kind) => {
    if (Kind === 3) return 'normal';
    if (Kind === 4) return 'premium';
    return '-';
  };

  const menuItems = [
    { text: t('home'), icon: <FaChartLine />, path: '/home' },
    { text: t('title'), icon: <FaUsers />, path: '/subscribers' },
    { text: t('signals.title'), icon: <FaChartLine />, path: '/signals' },
    { text: t('profile.title'), icon: <FaUser />, path: '/profile' },
  ];

  const drawer = (
    <Box sx={{ p: 2, bgcolor: '#fff' }}>
      <List>
        {menuItems.map((item) => (
          <ListItem
            key={item.path}
            component={Link}
            to={item.path}
            onClick={handleDrawerToggle}
            sx={{
              py: 1,
              '&:hover': {
                bgcolor: 'rgba(0,255,0,0.1)',
                boxShadow: '0 0 8px rgba(0,255,0,0.3)',
                transform: 'scale(1.02)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ color: '#1e3c72' }}>{item.icon}</Box>
              <ListItemText primary={item.text} />
            </Box>
          </ListItem>
        ))}
        <ListItem
          onClick={handleLogout}
          sx={{
            py: 1,
            '&:hover': {
              bgcolor: 'rgba(255,0,0,0.1)',
              boxShadow: '0 0 8px rgba(255,0,0,0.3)',
              transform: 'scale(1.02)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ color: '#1e3c72' }}><FaSignOutAlt /></Box>
            <ListItemText primary={t('logout')} />
          </Box>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(45deg, #1e3c72, #2a5298, rgba(0,255,0,0.3), rgba(255,0,0,0.3))',
        backgroundSize: '400% 400%',
        animation: 'backgroundGradient 15s ease infinite',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '5%',
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(0, 255, 0, 0.3) 0%, transparent 70%)',
          animation: 'pulse 6s infinite ease-in-out',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '10%',
          right: '5%',
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255, 0, 0, 0.3) 0%, transparent 70%)',
          animation: 'pulse 6s infinite ease-in-out reverse',
        }}
      />
      <style>
        {`
          @keyframes backgroundGradient {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          @keyframes pulse {
            0% { transform: scale(1); opacity: 0.5; }
            50% { transform: scale(1.2); opacity: 0.3; }
            100% { transform: scale(1); opacity: 0.5; }
          }
          @keyframes slideInLeft {
            0% { transform: translateX(-100px); opacity: 0; }
            100% { transform: translateX(0); opacity: 1; }
          }
          @keyframes slideInRight {
            0% { transform: translateX(100px); opacity: 0; }
            100% { transform: translateX(0); opacity: 1; }
          }
          @keyframes vibrate {
            0% { transform: translateX(0); }
            20% { transform: translateX(-2px); }
            40% { transform: translateX(2px); }
            60% { transform: translateX(-2px); }
            80% { transform: translateX(2px); }
            100% { transform: translateX(0); }
          }
          @keyframes winPulseGreen {
            0% { box-shadow: 0 0 0 0 rgba(0, 255, 0, 0.5); }
            50% { box-shadow: 0 0 15px 5px rgba(0, 255, 0, 0.3); }
            100% { box-shadow: 0 0 0 0 rgba(0, 255, 0, 0.5); }
          }
          @keyframes winPulseRed {
            0% { box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.5); }
            50% { box-shadow: 0 0 15px 5px rgba(255, 0, 0, 0.3); }
            100% { box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.5); }
          }
          @keyframes slideDown {
            0% { transform: translateY(-50px); opacity: 0; }
            100% { transform: translateY(0); opacity: 1; }
          }
          @keyframes gradientMove {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          @keyframes neonGlowGreen {
            0% { box-shadow: 0 0 5px rgba(0, 255, 0, 0.3); }
            50% { box-shadow: 0 0 20px rgba(0, 255, 0, 0.7); }
            100% { box-shadow: 0 0 5px rgba(0, 255, 0, 0.3); }
          }
          @keyframes neonGlowRed {
            0% { box-shadow: 0 0 5px rgba(255, 0, 0, 0.3); }
            50% { box-shadow: 0 0 20px rgba(255, 0, 0, 0.7); }
            100% { box-shadow: 0 0 5px rgba(255, 0, 0, 0.3); }
          }
          @keyframes softBounce {
            0% { transform: scale(1); }
            50% { transform: scale(0.95); }
            100% { transform: scale(1); }
          }
          @keyframes ripple {
            0% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.5); }
            100% { box-shadow: 0 0 0 20px rgba(255, 255, 255, 0); }
          }
          @keyframes typing {
            0% { width: 0; }
            100% { width: 100%; }
          }
          @keyframes glow {
            0% { text-shadow: 0 0 5px rgba(0, 255, 0, 0.3), 0 0 5px rgba(255, 0, 0, 0.3); }
            50% { text-shadow: 0 0 15px rgba(0, 255, 0, 0.7), 0 0 15px rgba(255, 0, 0, 0.7); }
            100% { text-shadow: 0 0 5px rgba(0, 255, 0, 0.3), 0 0 5px rgba(255, 0, 0, 0.3); }
          }
          @keyframes fadeInDown {
            0% { transform: translateY(-10px); opacity: 0; }
            100% { transform: translateY(0); opacity: 1; }
          }
          @keyframes fadeIn {
            0% { opacity: 0; }
            100% { opacity: 1; }
          }
        `}
      </style>
      <AppBar
        position="static"
        key={`appbar-${i18n.language}`}
        sx={{
          bgcolor: 'rgba(255,255,255,0.95)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          zIndex: 1300,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Typography
            variant="h6"
            key={`title-${i18n.language}`}
            sx={{
              color: '#1e3c72',
              fontWeight: 'bold',
              textShadow: '0 0 8px rgba(0,255,0,0.2)',
              animation: 'typing 2s steps(30, end) forwards, glow 2s infinite',
            }}
          >
            {t('title')}
          </Typography>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2, alignItems: 'center', direction: i18n.language === 'fa' ? 'rtl' : 'ltr' }}>
            {menuItems.map((item) => (
              <Button
                key={item.path}
                component={Link}
                to={item.path}
                startIcon={item.icon}
                sx={{
                  color: '#1e3c72',
                  textTransform: 'none',
                  fontSize: '1rem',
                  '&:hover': {
                    color: '#fff',
                    bgcolor: '#2a5298',
                    boxShadow: '0 0 10px rgba(0,255,0,0.4), 0 0 10px rgba(255,0,0,0.4)',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {item.text}
              </Button>
            ))}
            <Button
              startIcon={<FaSignOutAlt />}
              onClick={handleLogout}
              sx={{
                color: '#1e3c72',
                textTransform: 'none',
                fontSize: '1rem',
                '&:hover': {
                  color: '#fff',
                  bgcolor: '#d32f2f',
                  boxShadow: '0 0 10px rgba(255,0,0,0.5)',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              {t('logout')}
            </Button>
            <LanguageSwitcher />
          </Box>
          <IconButton
            color="inherit"
            edge="end"
            onClick={handleDrawerToggle}
            sx={{ display: { md: 'none' }, color: '#1e3c72' }}
          >
            <FaBars />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Drawer
        anchor={i18n.language === 'fa' ? 'right' : 'left'}
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{ '& .MuiDrawer-paper': { width: 240 } }}
      >
        {drawer}
      </Drawer>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, bgcolor: 'rgba(0,0,0,0.5)' }}
        open={loading}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{
            color: '#fff', // رنگ سفید برای هماهنگی با پس‌زمینه
            background: 'linear-gradient(90deg, #00ff00, #ff0000)', // گرادیانت سبز و قرمز
            borderRadius: '50%',
            animation: 'spin 1s linear infinite', // انیمیشن چرخش
          }}
        />
      </Backdrop>      
      <Box
        ref={stickyRef}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1200,
          bgcolor: 'rgba(255,255,255,0.95)',
          py: { xs: 0.3, sm: 2 },
          px: { xs: 0.5, sm: 4 },
          boxShadow: '0 2px 8px rgba(0,0,0,0.1), 0 0 10px rgba(0,255,0,0.2), 0 0 10px rgba(255,0,0,0.2)',
          borderRadius: 2,
          width: '100%',
          boxSizing: 'border-box',
          maxWidth: { xs: '100%', sm: 'lg' },
          mx: 'auto',
          contain: 'layout',
          animation: 'slideDown 0.5s ease-out forwards',
        }}
      >
        <Grid container spacing={{ xs: 1, sm: 1 }} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
            <Box sx={{ contain: 'layout', position: 'static', width: { xs: '100%', sm: '200px' } }}>
              <TextField
                fullWidth
                label={t('search')}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '& fieldset': { borderColor: '#2a5298' },
                    '&:hover fieldset': { borderColor: '#1e3c72' },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1e3c72',
                      boxShadow: '0 0 8px rgba(0,255,0,0.3)',
                    },
                    fontSize: { xs: '0.8rem', sm: '1rem' },
                    lineHeight: { xs: '1', sm: '1.5' },
                    height: { xs: '32px', sm: '56px' },
                    py: { xs: 0, sm: 1 },
                    transform: 'none',
                    position: 'static',
                  },
                  '& .MuiInputBase-root': {
                    transform: 'none',
                    position: 'static',
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.7rem', sm: '1rem' },
                    transform: { xs: 'translate(14px, 8px) scale(1)', sm: 'translate(14px, 16px) scale(1)' },
                    '&.MuiInputLabel-shrink': {
                      transform: { xs: 'translate(14px, -6px) scale(0.75)', sm: 'translate(14px, -6px) scale(0.75)' },
                    },
                    lineHeight: 1,
                  },
                  '& .Mui-focused .MuiInputLabel-root': {
                    transform: { xs: 'translate(14px, -6px) scale(0.75)', sm: 'translate(14px, -6px) scale(0.75)' },
                  },
                }}
              />
            </Box>
            <IconButton
              onClick={handleRefresh}
              sx={{
                color: '#1e3c72',
                '&:hover': {
                  bgcolor: '#e0f7fa',
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <FaSyncAlt />
            </IconButton>
            <IconButton
              onClick={handleAddUser}
              sx={{
                color: '#4caf50',
                '&:hover': {
                  bgcolor: '#e0f7fa',
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <FaUserPlus />
            </IconButton>
            <IconButton
              onClick={handleEditUser}
              disabled={selectedCardIndex === null}
              sx={{
                color: selectedCardIndex !== null ? '#ff9800' : '#bdbdbd',
                '&:hover': {
                  bgcolor: selectedCardIndex !== null ? '#e0f7fa' : 'transparent',
                  transform: selectedCardIndex !== null ? 'scale(1.1)' : 'none',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <FaUserEdit />
            </IconButton>
          </Grid>
          <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, justifyContent: i18n.language === 'fa' ? 'flex-start' : 'flex-end' }}>
              <IconButton
                onClick={handleSingleColumn}
                sx={{
                  color: viewMode === 'single' ? '#fff' : '#1e3c72',
                  bgcolor: viewMode === 'single' ? '#1e3c72' : 'transparent',
                  borderRadius: 2,
                  padding: { xs: '4px', sm: '6px' },
                  width: { xs: '32px', sm: '40px' },
                  height: { xs: '32px', sm: '40px' },
                  '&:hover': {
                    bgcolor: viewMode === 'single' ? '#1e3c72' : 'rgba(0,255,0,0.1)',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <FaThLarge sx={{ fontSize: { xs: '18px', sm: '24px' } }} />
              </IconButton>
              <IconButton
                onClick={handleDoubleColumn}
                sx={{
                  color: viewMode === 'double' ? '#fff' : '#1e3c72',
                  bgcolor: viewMode === 'double' ? '#1e3c72' : 'transparent',
                  borderRadius: 2,
                  padding: { xs: '4px', sm: '6px' },
                  width: { xs: '32px', sm: '40px' },
                  height: { xs: '32px', sm: '40px' },
                  '&:hover': {
                    bgcolor: viewMode === 'double' ? '#1e3c72' : 'rgba(0,255,0,0.1)',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <FaTh sx={{ fontSize: { xs: '18px', sm: '24px' } }} />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Container sx={{ py: { xs: 2, sm: 4 }, px: { xs: 1, sm: 2 }, width: '100%', maxWidth: '100% !important', boxSizing: 'border-box' }}>
        <Fade in timeout={600}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: `
                0 8px 24px rgba(0,0,0,0.2),
                0 0 20px rgba(0,255,0,0.2),
                0 0 20px rgba(255,0,0,0.2)
              `,
              bgcolor: 'rgba(255,255,255,0.95)',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 4 }, width: '100%', boxSizing: 'border-box' }}>
              <Box sx={{ overflow: 'hidden', whiteSpace: 'nowrap' }}>
                <Typography
                  variant="h4"
                  sx={{
                    color: '#1e3c72',
                    fontWeight: 'bold',
                    mb: 3,
                    textShadow: '0 0 10px rgba(0,255,0,0.3)',
                    textAlign: 'center',
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    display: 'inline-block',
                    animation: 'typing 2s steps(30, end) forwards, glow 2s infinite',
                  }}
                >
                  {t('title')}
                </Typography>
              </Box>
              <Box sx={{ mt: 3, width: '100%' }}>
                {filteredSubscribers.length > 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 2,
                      width: '100%',
                      boxSizing: 'border-box',
                      overflowX: 'auto',
                      px: 0,
                    }}
                  >
                    {filteredSubscribers.map((subscriber, index) => {
                      const cardsPerRow = viewMode === 'double' ? 2 : 1;
                      const gapTotal = (cardsPerRow - 1) * 16;
                      const cardWidth = cardsPerRow === 1 ? '100%' : `calc((100% - ${gapTotal}px) / 2)`;

                      return (
                        <Box
                          key={index}
                          sx={{
                            width: { xs: '100%', md: cardWidth },
                            minWidth: { xs: '100%', md: '250px' },
                            animation: i18n.language === 'fa' ? 'slideInRight 0.5s ease-out forwards' : 'slideInLeft 0.5s ease-out forwards',
                            animationDelay: `${index * 0.1}s`,
                            opacity: 0,
                            boxSizing: 'border-box',
                            flexShrink: 0,
                            display: 'flex',
                            direction: i18n.language === 'fa' ? 'rtl' : 'ltr',
                            alignItems: 'stretch',
                          }}
                        >
                          <Card
                            onClick={() => handleCardSelect(index)}
                            sx={{
                              flex: 1,
                              borderRadius: 4,
                              bgcolor: 'rgba(255,255,255,0.9)',
                              background: 'linear-gradient(45deg, rgba(255,255,255,0.9), rgba(255,255,255,0.9))',
                              boxShadow: `
                                0 4px 12px rgba(0,0,0,0.1),
                                0 0 10px rgba(0,255,0,0.2),
                                0 0 10px rgba(255,0,0,0.2)
                              `,
                              border: selectedCardIndex === index ? '2px solid #ff9800' :
                                searchText && (
                                  (subscriber.Name?.toLowerCase().includes(searchText.toLowerCase()) || false) ||
                                  (subscriber.Family?.toLowerCase().includes(searchText.toLowerCase()) || false) ||
                                  (subscriber.EndDate?.toLowerCase().includes(searchText.toLowerCase()) || false) ||
                                  (subscriber.TotalProfit?.toString().toLowerCase().includes(searchText.toLowerCase()) || false) ||
                                  (subscriber.TotalWin?.toString().toLowerCase().includes(searchText.toLowerCase()) || false) ||
                                  (subscriber.TotalLoss?.toString().toLowerCase().includes(searchText.toLowerCase()) || false)
                                ) ? '2px solid #4caf50' : 'none',
                              '&:hover': {
                                transform: 'translateY(-5px)',
                                boxShadow: `
                                  0 8px 20px rgba(0,0,0,0.2),
                                  0 0 20px rgba(0,255,0,0.4),
                                  0 0 20px rgba(255,0,0,0.4)
                                `,
                                animation: 'vibrate 0.3s ease-in-out',
                                '& .MuiTypography-root': {
                                  color: '#1e3c72',
                                },
                              },
                              transition: 'all 0.3s ease-in-out',
                              height: '100%',
                              width: '100%',
                              boxSizing: 'border-box',
                              cursor: 'pointer',
                            }}
                          >
                            
                            <CardContent sx={{ p: 2, direction: i18n.language === 'fa' ? 'rtl' : 'ltr' }}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                               
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontWeight: 'bold',
                                        color: '#1e3c72',
                                        fontSize: { xs: '0.9rem', sm: '1rem' },
                                      }}
                                    >
                                      {subscriber.Name || '-'} {subscriber.Family || '-'}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5 }}>
                                    {getTypeLabel(subscriber.UKind) === 'normal' ? (
                                      <Person sx={{ color: '#1e3c72', fontSize: '1rem' }} />
                                    ) : (
                                      <EmojiEvents sx={{ color: '#FFD700', fontSize: '1rem' }} />
                                    )}
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: '#1e3c72',
                                        fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                      }}
                                    >
                                      {t(`type.${getTypeLabel(subscriber.UKind)}`)}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                                    <Button
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCopyUserID(subscriber.UserID);
                                      }}
                                      sx={{
                                        bgcolor: '#e0e0e0',
                                        color: '#000',
                                        fontSize: { xs: '0.6rem', sm: '0.8rem' },
                                        padding: { xs: '2px 6px', sm: '4px 8px' },
                                        minWidth: '70px',
                                        '&:hover': {
                                          bgcolor: '#d5d5d5',
                                          transform: 'scale(1.05)',
                                        },
                                        transition: 'all 0.2s ease-in-out',
                                      }}
                                    >
                                      {t('copyCode')}
                                    </Button>
                                    <Switch
                                      checked={subscriber.Active}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        handleToggleActive(index);
                                      }}
                                      sx={{
                                        '& .MuiSwitch-switchBase': {
                                          color: '#d32f2f',
                                          '&.Mui-checked': {
                                            color: '#4caf50',
                                          },
                                          '&.Mui-checked + .MuiSwitch-track': {
                                            backgroundColor: '#4caf50',
                                          },
                                        },
                                        '& .MuiSwitch-track': {
                                          backgroundColor: '#d32f2f',
                                        },
                                      }}
                                    />
                                  </Box>
                                </Box>
                              
                              
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: '#1e3c72',
                                        fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                      }}
                                    >
                                      <strong>{t('nationalCode')}:</strong> {subscriber.NationalCode || '-'}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: '#1e3c72',
                                        fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                      }}
                                    >
                                      <strong>{t('mobileNo')}:</strong> {subscriber.MobileNO || '-'}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ flex: 1 }} />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: '#1e3c72',
                                        fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                      }}
                                    >
                                      <strong>{t('startDate')}:</strong> {formatDate(subscriber.StartDate)}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: '#1e3c72',
                                        fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                      }}
                                    >
                                      <strong>{t('endDate')}:</strong> {formatDate(subscriber.EndDate)}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ flex: 1 }} />
                                </Box>
                                
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: '#1e3c72',
                                      fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                    }}
                                  >
                                    <strong>{t('totalProfit')}:</strong> {subscriber.TotalProfit || '0'}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: '#4caf50',
                                      fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                    }}
                                  >
                                    <strong>{t('totalWin')}:</strong> {subscriber.TotalWin || '0'}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: '#d32f2f',
                                      fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                    }}
                                  >
                                    <strong>{t('totalLoss')}:</strong> {subscriber.TotalLoss || '0'}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, justifyContent: i18n.language === 'fa' ? 'flex-end' : 'flex-start' }}>
                                  <Button
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSignalClick(subscriber.UserID, user.UserID, subscriber.UKind);
                                    }}
                                    sx={{
                                      bgcolor: '#1e3c72',
                                      color: '#fff',
                                      fontSize: { xs: '0.6rem', sm: '0.8rem' },
                                      padding: { xs: '2px 6px', sm: '4px 8px' },
                                      '&:hover': {
                                        bgcolor: '#2a5298',
                                        transform: 'scale(1.05)',
                                      },
                                      transition: 'all 0.2s ease-in-out',
                                    }}
                                  >
                                    {t('signals.title')}
                                  </Button>
                                  <Button
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlePaymentClick(subscriber.UserID);
                                    }}
                                    sx={{
                                      bgcolor: '#4caf50',
                                      color: '#fff',
                                      fontSize: { xs: '0.6rem', sm: '0.8rem' },
                                      padding: { xs: '2px 6px', sm: '4px 8px' },
                                      '&:hover': {
                                        bgcolor: '#66bb6a',
                                        transform: 'scale(1.05)',
                                      },
                                      transition: 'all 0.2s ease-in-out',
                                    }}
                                  >
                                    {t('payment')}
                                  </Button>
                                  {(Number(subscriber.TotalProfit || 0) !== 0 || Number(subscriber.TotalWin || 0) !== 0 || Number(subscriber.TotalLoss || 0) !== 0) && (
                                    <Button
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleProfitsClick(subscriber.UserID);
                                      }}
                                      sx={{
                                        bgcolor: '#ff9800',
                                        color: '#fff',
                                        fontSize: { xs: '0.6rem', sm: '0.8rem' },
                                        padding: { xs: '2px 6px', sm: '4px 8px' },
                                        '&:hover': {
                                          bgcolor: '#ffb300',
                                          transform: 'scale(1.05)',
                                        },
                                        transition: 'all 0.2s ease-in-out',
                                      }}
                                    >
                                      {t('profits')}
                                    </Button>
                                  )}
                                </Box>
                              </Box>
                            </CardContent>



                            
                          </Card>
                        </Box>
                      );
                    })}
                  </Box>
                ) : null}
              </Box>
            </CardContent>

            
          </Card>
        </Fade>
        <Modal
          open={openAddUserModal}
          onClose={handleCloseAddUserModal}
          aria-labelledby="add-user-modal"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Paper
            sx={{
              bgcolor: 'rgba(255,255,255,0.95)',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              p: { xs: 2, sm: 3 },
              width: { xs: '90%', sm: '450px' },
              maxWidth: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
              direction: i18n.language === 'fa' ? 'rtl' : 'ltr',
              position: 'relative',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: '#1e3c72',
                fontWeight: 'bold',
                mb: 2,
                textAlign: 'center',
              }}
            >
              {t('addUser')}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <TextField
                      label={t('name')}
                      value={newUser.name}
                      onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                      fullWidth
                      variant="outlined"
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': { borderColor: '#2a5298' },
                          '&:hover fieldset': { borderColor: '#1e3c72' },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1e3c72',
                            boxShadow: '0 0 8px rgba(0,255,0,0.3)',
                          },
                          height: '40px',
                          minWidth: '100%',
                          maxWidth: '100%',
                          fontSize: '0.85rem',
                          padding: '0 8px',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.85rem',
                          transform: 'translate(14px, 10px) scale(1)',
                          '&.MuiInputLabel-shrink': {
                            transform: 'translate(14px, -6px) scale(0.75)',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label={t('family')}
                      value={newUser.family}
                      onChange={(e) => setNewUser({ ...newUser, family: e.target.value })}
                      fullWidth
                      variant="outlined"
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': { borderColor: '#2a5298' },
                          '&:hover fieldset': { borderColor: '#1e3c72' },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1e3c72',
                            boxShadow: '0 0 8px rgba(0,255,0,0.3)',
                          },
                          height: '40px',
                          minWidth: '100%',
                          maxWidth: '100%',
                          fontSize: '0.85rem',
                          padding: '0 8px',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.85rem',
                          transform: 'translate(14px, 10px) scale(1)',
                          '&.MuiInputLabel-shrink': {
                            transform: 'translate(14px, -6px) scale(0.75)',
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={1} sx={{ alignItems: 'center' }}>
                  <Grid item xs={6}>
                    <DatePicker
                      label={t('startDate')}
                      value={newUser.startDate}
                      onChange={(newValue) => setNewUser({ ...newUser, startDate: newValue })}
                      format="yyyy-MM-dd"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: 'outlined',
                          size: 'small',
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '& fieldset': { borderColor: '#2a5298' },
                              '&:hover fieldset': { borderColor: '#1e3c72' },
                              '&.Mui-focused fieldset': {
                                borderColor: '#1e3c72',
                                boxShadow: '0 0 8px rgba(0,255,0,0.3)',
                              },
                              height: '40px',
                              minWidth: '100%',
                              maxWidth: '100%',
                              fontSize: '0.85rem',
                              padding: '0 8px',
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: '0.85rem',
                              transform: 'translate(14px, 10px) scale(1)',
                              '&.MuiInputLabel-shrink': {
                                transform: 'translate(14px, -6px) scale(0.75)',
                              },
                            },
                            '& .MuiInputAdornment-root': {
                              marginLeft: '4px',
                              '& .MuiButtonBase-root': {
                                padding: '0',
                                width: '20px',
                                height: '20px',
                              },
                              '& .MuiSvgIcon-root': {
                                fontSize: '1rem',
                              },
                            },
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DatePicker
                      label={t('endDate')}
                      value={newUser.endDate}
                      onChange={(newValue) => setNewUser({ ...newUser, endDate: newValue })}
                      format="yyyy-MM-dd"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: 'outlined',
                          size: 'small',
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '& fieldset': { borderColor: '#2a5298' },
                              '&:hover fieldset': { borderColor: '#1e3c72' },
                              '&.Mui-focused fieldset': {
                                borderColor: '#1e3c72',
                                boxShadow: '0 0 8px rgba(255,0,0,0.3)',
                              },
                              height: '40px',
                              minWidth: '100%',
                              maxWidth: '100%',
                              fontSize: '0.85rem',
                              padding: '0 8px',
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: '0.85rem',
                              transform: 'translate(14px, 10px) scale(1)',
                              '&.MuiInputLabel-shrink': {
                                transform: 'translate(14px, -6px) scale(0.75)',
                              },
                            },
                            '& .MuiInputAdornment-root': {
                              marginLeft: '4px',
                              '& .MuiButtonBase-root': {
                                padding: '0',
                                width: '20px',
                                height: '20px',
                              },
                              '& .MuiSvgIcon-root': {
                                fontSize: '1rem',
                              },
                            },
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={hasParentID ? 4 : 6}>
                    <TextField
                      label={t('nationalCode')}
                      value={newUser.nationalCode}
                      onChange={(e) => setNewUser({ ...newUser, nationalCode: e.target.value })}
                      fullWidth
                      variant="outlined"
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': { borderColor: '#2a5298' },
                          '&:hover fieldset': { borderColor: '#1e3c72' },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1e3c72',
                            boxShadow: '0 0 8px rgba(0,255,0,0.3)',
                          },
                          height: '40px',
                          minWidth: '100%',
                          maxWidth: '100%',
                          fontSize: '0.85rem',
                          padding: '0 8px',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.85rem',
                          transform: 'translate(14px, 10px) scale(1)',
                          '&.MuiInputLabel-shrink': {
                            transform: 'translate(14px, -6px) scale(0.75)',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={hasParentID ? 4 : 6}>
                    <TextField
                      label={t('mobileNo')}
                      value={newUser.mobileNO}
                      onChange={(e) => setNewUser({ ...newUser, mobileNO: e.target.value })}
                      fullWidth
                      variant="outlined"
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': { borderColor: '#2a5298' },
                          '&:hover fieldset': { borderColor: '#1e3c72' },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1e3c72',
                            boxShadow: '0 0 8px rgba(0,255,0,0.3)',
                          },
                          height: '40px',
                          minWidth: '100%',
                          maxWidth: '100%',
                          fontSize: '0.85rem',
                          padding: '0 8px',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.85rem',
                          transform: 'translate(14px, 10px) scale(1)',
                          '&.MuiInputLabel-shrink': {
                            transform: 'translate(14px, -6px) scale(0.75)',
                          },
                        },
                      }}
                    />
                  </Grid>
                  {hasParentID && (
                    <Grid item xs={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={newUser.isVIP}
                            onChange={(e) => setNewUser({ ...newUser, isVIP: e.target.checked })}
                            sx={{
                              fontSize: '0.85rem',
                              padding: '4px',
                            }}
                          />
                        }
                        label={t('isVIP')}
                        sx={{
                          color: '#1e3c72',
                          '& .MuiTypography-root': {
                            fontSize: '0.85rem',
                          },
                          margin: 0,
                        }}
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>
            </LocalizationProvider>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, gap: 2 }}>
              <Button
                onClick={handleCloseAddUserModal}
                sx={{
                  bgcolor: '#d32f2f',
                  color: '#fff',
                  fontSize: { xs: '0.8rem', sm: '1rem' },
                  padding: { xs: '4px 12px', sm: '6px 16px' },
                  flex: 1,
                  '&:hover': {
                    bgcolor: '#ef5350',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {t('close')}
              </Button>
              <Button
                onClick={handleAddUserSubmit}
                disabled={loading || !newUser.name || !newUser.family || !newUser.startDate || !newUser.endDate || !newUser.nationalCode || !newUser.mobileNO}
                sx={{
                  bgcolor: '#4caf50',
                  color: '#fff',
                  fontSize: { xs: '0.8rem', sm: '1rem' },
                  padding: { xs: '4px 12px', sm: '6px 16px' },
                  flex: 1,
                  '&:hover': {
                    bgcolor: '#66bb6a',
                    transform: 'scale(1.05)',
                  },
                  '&:disabled': {
                    bgcolor: '#bdbdbd',
                    color: '#fff',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {loading ? (
              <CircularProgress
                size={20}
                sx={{
                  color: 'transparent',
                  background: 'linear-gradient(90deg, #00ff00, #ff0000)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}
              />
            ) : (
              t('submit')
            )}

              </Button>
            </Box>
          </Paper>
        </Modal>
        <Modal
          open={openEditUserModal}
          onClose={handleCloseEditUserModal}
          aria-labelledby="edit-user-modal"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Paper
            sx={{
              bgcolor: 'rgba(255,255,255,0.95)',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              p: { xs: 2, sm: 3 },
              width: { xs: '90%', sm: '450px' },
              maxWidth: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
              direction: i18n.language === 'fa' ? 'rtl' : 'ltr',
              position: 'relative',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: '#1e3c72',
                fontWeight: 'bold',
                mb: 2,
                textAlign: 'center',
              }}
            >
              {t('editUser')}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <TextField
                      label={t('name')}
                      value={editUser.name}
                      onChange={(e) => setEditUser({ ...editUser, name: e.target.value })}
                      fullWidth
                      variant="outlined"
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': { borderColor: '#2a5298' },
                          '&:hover fieldset': { borderColor: '#1e3c72' },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1e3c72',
                            boxShadow: '0 0 8px rgba(0,255,0,0.3)',
                          },
                          height: '40px',
                          minWidth: '100%',
                          maxWidth: '100%',
                          fontSize: '0.85rem',
                          padding: '0 8px',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.85rem',
                          transform: 'translate(14px, 10px) scale(1)',
                          '&.MuiInputLabel-shrink': {
                            transform: 'translate(14px, -6px) scale(0.75)',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label={t('family')}
                      value={editUser.family}
                      onChange={(e) => setEditUser({ ...editUser, family: e.target.value })}
                      fullWidth
                      variant="outlined"
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': { borderColor: '#2a5298' },
                          '&:hover fieldset': { borderColor: '#1e3c72' },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1e3c72',
                            boxShadow: '0 0 8px rgba(0,255,0,0.3)',
                          },
                          height: '40px',
                          minWidth: '100%',
                          maxWidth: '100%',
                          fontSize: '0.85rem',
                          padding: '0 8px',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.85rem',
                          transform: 'translate(14px, 10px) scale(1)',
                          '&.MuiInputLabel-shrink': {
                            transform: 'translate(14px, -6px) scale(0.75)',
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={1} sx={{ alignItems: 'center' }}>
                  <Grid item xs={6}>
                    <DatePicker
                      label={t('startDate')}
                      value={editUser.startDate}
                      onChange={(newValue) => setEditUser({ ...editUser, startDate: newValue })}
                      format="yyyy-MM-dd"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: 'outlined',
                          size: 'small',
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '& fieldset': { borderColor: '#2a5298' },
                              '&:hover fieldset': { borderColor: '#1e3c72' },
                              '&.Mui-focused fieldset': {
                                borderColor: '#1e3c72',
                                boxShadow: '0 0 8px rgba(0,255,0,0.3)',
                              },
                              height: '40px',
                              minWidth: '100%',
                              maxWidth: '100%',
                              fontSize: '0.85rem',
                              padding: '0 8px',
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: '0.85rem',
                              transform: 'translate(14px, 10px) scale(1)',
                              '&.MuiInputLabel-shrink': {
                                transform: 'translate(14px, -6px) scale(0.75)',
                              },
                            },
                            '& .MuiInputAdornment-root': {
                              marginLeft: '4px',
                              '& .MuiButtonBase-root': {
                                padding: '0',
                                width: '20px',
                                height: '20px',
                              },
                              '& .MuiSvgIcon-root': {
                                fontSize: '1rem',
                              },
                            },
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DatePicker
                      label={t('endDate')}
                      value={editUser.endDate}
                      onChange={(newValue) => setEditUser({ ...editUser, endDate: newValue })}
                      format="yyyy-MM-dd"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: 'outlined',
                          size: 'small',
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '& fieldset': { borderColor: '#2a5298' },
                              '&:hover fieldset': { borderColor: '#1e3c72' },
                              '&.Mui-focused fieldset': {
                                borderColor: '#1e3c72',
                                boxShadow: '0 0 8px rgba(255,0,0,0.3)',
                              },
                              height: '40px',
                              minWidth: '100%',
                              maxWidth: '100%',
                              fontSize: '0.85rem',
                              padding: '0 8px',
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: '0.85rem',
                              transform: 'translate(14px, 10px) scale(1)',
                              '&.MuiInputLabel-shrink': {
                                transform: 'translate(14px, -6px) scale(0.75)',
                              },
                            },
                            '& .MuiInputAdornment-root': {
                              marginLeft: '4px',
                              '& .MuiButtonBase-root': {
                                padding: '0',
                                width: '20px',
                                height: '20px',
                              },
                              '& .MuiSvgIcon-root': {
                                fontSize: '1rem',
                              },
                            },
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={hasParentID ? 4 : 6}>
                    <TextField
                      label={t('nationalCode')}
                      value={editUser.nationalCode}
                      onChange={(e) => setEditUser({ ...editUser, nationalCode: e.target.value })}
                      fullWidth
                      variant="outlined"
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': { borderColor: '#2a5298' },
                          '&:hover fieldset': { borderColor: '#1e3c72' },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1e3c72',
                            boxShadow: '0 0 8px rgba(0,255,0,0.3)',
                          },
                          height: '40px',
                          minWidth: '100%',
                          maxWidth: '100%',
                          fontSize: '0.85rem',
                          padding: '0 8px',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.85rem',
                          transform: 'translate(14px, 10px) scale(1)',
                          '&.MuiInputLabel-shrink': {
                            transform: 'translate(14px, -6px) scale(0.75)',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={hasParentID ? 4 : 6}>
                    <TextField
                      label={t('mobileNo')}
                      value={editUser.mobileNO}
                      onChange={(e) => setEditUser({ ...editUser, mobileNO: e.target.value })}
                      fullWidth
                      variant="outlined"
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': { borderColor: '#2a5298' },
                          '&:hover fieldset': { borderColor: '#1e3c72' },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1e3c72',
                            boxShadow: '0 0 8px rgba(0,255,0,0.3)',
                          },
                          height: '40px',
                          minWidth: '100%',
                          maxWidth: '100%',
                          fontSize: '0.85rem',
                          padding: '0 8px',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.85rem',
                          transform: 'translate(14px, 10px) scale(1)',
                          '&.MuiInputLabel-shrink': {
                            transform: 'translate(14px, -6px) scale(0.75)',
                          },
                        },
                      }}
                    />
                  </Grid>
                  {hasParentID && (
                    <Grid item xs={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={editUser.isVIP}
                            onChange={(e) => setEditUser({ ...editUser, isVIP: e.target.checked })}
                            sx={{
                              fontSize: '0.85rem',
                              padding: '4px',
                            }}
                          />
                        }
                        label={t('isVIP')}
                        sx={{
                          color: '#1e3c72',
                          '& .MuiTypography-root': {
                            fontSize: '0.85rem',
                          },
                          margin: 0,
                        }}
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>
            </LocalizationProvider>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, gap: 2 }}>
              <Button
                onClick={handleCloseEditUserModal}
                sx={{
                  bgcolor: '#d32f2f',
                  color: '#fff',
                  fontSize: { xs: '0.8rem', sm: '1rem' },
                  padding: { xs: '4px 12px', sm: '6px 16px' },
                  flex: 1,
                  '&:hover': {
                    bgcolor: '#ef5350',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {t('close')}
              </Button>
              <Button
                onClick={handleEditUserSubmit}
                disabled={loading || !editUser.name || !editUser.family || !editUser.startDate || !editUser.endDate || !editUser.nationalCode || !editUser.mobileNO}
                sx={{
                  bgcolor: '#4caf50',
                  color: '#fff',
                  fontSize: { xs: '0.8rem', sm: '1rem' },
                  padding: { xs: '4px 12px', sm: '6px 16px' },
                  flex: 1,
                  '&:hover': {
                    bgcolor: '#66bb6a',
                    transform: 'scale(1.05)',
                  },
                  '&:disabled': {
                    bgcolor: '#bdbdbd',
                    color: '#fff',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : t('submit')}
              </Button>
            </Box>
          </Paper>
        </Modal>
        <Modal
          open={openSignalsModal}
          onClose={handleCloseSignalsModal}
          aria-labelledby="signals-modal"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ width: '100%', maxWidth: '600px', bgcolor: 'background.paper', p: 2 }}>
            <UserSignals userID={selectedUserID} parentID={selectedParentID} userKind={selectedUserKind} />
          </Box>
        </Modal>
        <Modal
          open={openPaymentModal}
          onClose={handleClosePaymentModal}
          aria-labelledby="payment-modal"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ width: '100%', maxWidth: '350px', bgcolor: 'background.paper', p: 2 }}>
            <UserPayment userID={selectedUserID} />
          </Box>
        </Modal>
       
           
             
        <Modal
          open={openProfitsModal}
          onClose={handleCloseProfitsModal}
          aria-labelledby="profits-modal"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{  width: '100%', maxWidth: '600px', bgcolor: 'background.paper', p: 2 }}>
            <UserProfits userID={selectedUserID} />
          </Box>
        </Modal>


        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: i18n.language === 'fa' ? 'right' : 'left' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{
              width: '100%',
              bgcolor: snackbarSeverity === 'success' ? '#4caf50' : snackbarSeverity === 'error' ? '#d32f2f' : snackbarSeverity === 'warning' ? '#ff9800' : '#0288d1',
              color: '#fff',
              '& .MuiAlert-icon': {
                color: '#fff',
              },
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Subscribers;